import React, { useState } from "react";
import { Grid, Stack, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import GenericCardHeader from "@/components/GenericCardHeader";
import GetImages from "@/components/GetImages";
import Button from "@/components/Button";
import Confirmation from "@/components/Confirmation";
import PrescriptionPreview from "@/components/Medicines/PrescriptionPreview";
import TruncatedText from "@/components/TruncatedText";

import { style } from "./style";

import {
  IAllOrderTypeEnums,
  ICompleteOrPendingMedicineOrder,
  IMedicineOrderType,
  IOrderStatus,
} from "@/models/ApiRequestResponse/patient";
import { IButtonVariant } from "@/models/button";
import { IOrderStatusEnum } from "@/models/ApiRequestResponse/pharmacy";
import { IAllOrderListProps } from "@/models/patient";
import { IRedirectAfterLoginType, ISessionKeys } from "@/models";

import {
  formatPhoneNumber,
  getDateFormat,
  openClinicMapLocation,
} from "@/utils";
import { handleUpdateOrderApi } from "@/utils/apis/pharmacy";
import { INotifyEnum, notify } from "@/utils/toaster";
import { storage } from "@/utils/Storage";
import { supportNumber } from "@/utils/Config";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  allOrders,
  fetchAllOrdersList,
  fetchHomeModules,
  setAllOrders,
  setAllOrderStatusUpdate,
} from "@/store/slices/patient/home";
import {
  config,
  handleIsInfiniteScrollFilterChanges,
  setIsShowUniversalSearch,
  setRedirectAfterLogin,
} from "@/store/slices/config";
import {
  setMedicineDeliveryType,
  setMedicineOrderPlacedRequest,
  setSelectedPharmacy,
} from "@/store/slices/patient/medicine";

interface ICompleteOrPendingMedicineOrderProps
  extends ICompleteOrPendingMedicineOrder {
  filters?: IAllOrderListProps;
}

const MedicineCard: React.FC<ICompleteOrPendingMedicineOrderProps> = (
  props
) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] =
    useState<boolean>(false);

  const {
    id,
    name,
    type,
    status,
    createdat,
    address,
    city,
    latitude,
    longitude,
    tracking_id,
    delivery_partner,
    filters,
    order_no,
    gender,
    age,
    pincode,
    patientName,
    pharmacyid,
    pharmacyname,
    pharmacyprofilephoto,
    start_time,
    end_time,
    deliveryEnabled,
    pickupEnabled,
    distance,
    s3Url,
  } = props;

  const intl = useIntl();
  const navigate = useNavigate();

  const userId = storage.getSessionToken(ISessionKeys.USER_ID);

  const allOrdersList = useAppSelector(allOrders);
  const { isShowUniversalSearch } = useAppSelector(config) || {};
  const dispatch = useAppDispatch();

  const cancelOrderModalHandler = (): void =>
    setIsCancelModalOpen(!isCancelModalOpen);

  const medicineOrderCancelHandler: () => Promise<void> = async () => {
    const cancelOrderResponse = await handleUpdateOrderApi({
      orderId: id,
      status: IOrderStatusEnum.CANCELLED,
    });
    if ("result" in cancelOrderResponse) {
      notify(
        INotifyEnum.SUCCESS,
        intl.formatMessage({
          id: "order_cancelled",
        })
      );
      cancelOrderModalHandler();
      if (filters) {
        dispatch(setAllOrders([]));
        dispatch(handleIsInfiniteScrollFilterChanges());
        dispatch(fetchAllOrdersList({ ...filters, offset: "0" }));
      } else if (allOrdersList?.length === 0) {
        dispatch(fetchHomeModules({ userId: userId ?? "" }));
      } else {
        dispatch(
          setAllOrderStatusUpdate({
            ...props,
            status: IOrderStatusEnum.CANCELLED,
          })
        );
      }
    } else {
      notify(INotifyEnum.ERROR, cancelOrderResponse?.message);
    }
  };

  const bookAgainHandler = (): void => {
    dispatch(
      setSelectedPharmacy({
        id: pharmacyid,
        name: pharmacyname,
        city: city,
        address: address,
        pincode: pincode,
        deliveryEnabled,
        pickupEnabled,
        profilePhoto: pharmacyprofilephoto,
        latitude: latitude,
        longitude: longitude,
        startTime: start_time,
        endTime: end_time,
        distance,
      })
    );
    dispatch(
      setMedicineOrderPlacedRequest({
        key: "pharmacyId",
        value: pharmacyid,
      })
    );
    dispatch(
      setRedirectAfterLogin({
        type: IRedirectAfterLoginType.MEDICINES,
      })
    );
    dispatch(setMedicineDeliveryType(type));
    navigate("/medicines", {
      state: { step: 1 },
    });
    if (isShowUniversalSearch) {
      dispatch(setIsShowUniversalSearch(false));
    }
  };

  const openDirectionHandler = (): void =>
    openClinicMapLocation(latitude ?? 0, longitude ?? 0);

  return (
    <>
      <Stack sx={style.cardContainer}>
        <GenericCardHeader
          orderStatus={status}
          doctorId={""}
          name={name}
          bio={`${
            type === IMedicineOrderType.HOME_DELIVERY
              ? intl.formatMessage({
                  id: "home_delivery",
                  defaultMessage: "Home Delivery",
                })
              : intl.formatMessage({
                  id: "self_pickup",
                  defaultMessage: "Selfpickup",
                })
          } • Order Id ${order_no}`}
          imageSvg={
            <Grid item sx={style.headerIcon}>
              <GetImages name="MedicineBottleIcon" height={"38"} width={"26"} />
            </Grid>
          }
          orderType={IAllOrderTypeEnums.MEDICINE}
        />

        <Grid item sx={style.borderLine}>
          <Typography
            component={"p"}
            color={"secondary"}
            variant="caption"
            sx={style.testPatientDetail}
            mt={"0.8rem"}
          >
            <GetImages name={"SmileOrderIcon"} width="18" height="16" />
            {`${patientName}, ${gender}, ${age}`}
          </Typography>
          <Stack direction={"row"} alignItems={"center"} gap={0.6}>
            <GetImages
              name={"ScheduleSelectedSmallIcon"}
              width="18"
              height="16"
            />
            <Typography variant={"caption"} color={"secondary"}>
              <FormattedMessage
                id="order_placed_on"
                defaultMessage="Order placed on"
              />{" "}
              {getDateFormat(createdat, "DD MMMM YYYY, hh:mm A")}
            </Typography>
          </Stack>
          <Typography
            component={"p"}
            color={"secondary"}
            variant="caption"
            sx={{ ...style.testPatientDetail, ...style.hideShowBlock }}
          >
            <GetImages name={"TelePhoneIcon"} width="18" height="16" />
            <FormattedMessage
              id="contact_support"
              defaultMessage="Contact Support"
            />{" "}
            - {formatPhoneNumber(supportNumber)}
          </Typography>
          <Grid
            item
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack direction={"row"} alignItems={"center"} gap={0.6}>
              <TruncatedText
                text={`${address}, ${city} - ${pincode}`}
                muiStyles={style.detail}
                maxLength={30}
                icon={
                  <GetImages
                    name={"LocationSmallIcon"}
                    width="18"
                    height="16"
                  />
                }
              />
            </Stack>
            {type === IMedicineOrderType.PICKUP &&
              (status === IOrderStatus.CONFIRMED ||
                status === IOrderStatus.PENDING) && (
                <Grid
                  item
                  sx={style.clinicLocation}
                  onClick={openDirectionHandler}
                >
                  <GetImages
                    name="HospitalLocationIcon"
                    width="16"
                    height="16"
                  />
                  <Typography component={"p"} sx={style.mapText}>
                    <FormattedMessage id="maps" defaultMessage="Maps" />
                  </Typography>
                </Grid>
              )}
          </Grid>
          {status === IOrderStatus.COMPLETED && tracking_id && (
            <Stack direction={"row"} alignItems={"center"} gap={0.6}>
              <GetImages name={"LikeIcon"} width="18" height="16" />
              <Typography component={"p"} sx={style.footerText}>
                {delivery_partner},{" "}
                <FormattedMessage
                  id="tracking_id"
                  defaultMessage="Tracking ID"
                />{" "}
                - {tracking_id}
              </Typography>
            </Stack>
          )}

          <Stack direction={"row"} alignItems={"center"} gap={1} width={"100%"}>
            <Grid item sx={style.buttonContainer}>
              {(status === IOrderStatus.PENDING ||
                status === IOrderStatus.CONFIRMED) && (
                <Grid item>
                  <Button
                    variant={IButtonVariant.WHITE}
                    btnTrigger={cancelOrderModalHandler}
                  >
                    <FormattedMessage id="cancel" defaultMessage="Cancel" />
                  </Button>
                </Grid>
              )}

              <Grid item sx={style.prescriptionButton}>
                <Button
                  variant={IButtonVariant.WHITE}
                  btnTrigger={() => setIsPrescriptionModalOpen(true)}
                >
                  <GetImages name={"PrescriptionIcon"} width="26" height="20" />
                  <FormattedMessage
                    id="prescription"
                    defaultMessage="Prescription"
                  />
                </Button>
              </Grid>
              {type === IMedicineOrderType.PICKUP &&
                (status === IOrderStatus.CONFIRMED ||
                  status === IOrderStatus.PENDING) && (
                  <Grid item sx={style.directionButton}>
                    <Button
                      variant={IButtonVariant.WHITE}
                      btnTrigger={openDirectionHandler}
                    >
                      <GetImages
                        name="LocationAccessSmallIcon"
                        width="26"
                        height="20"
                      />
                      <FormattedMessage
                        id="direction"
                        defaultMessage="Direction"
                      />
                    </Button>
                  </Grid>
                )}
              {status !== IOrderStatus.CANCELLED && (
                <Grid
                  item
                  sx={{
                    ...style.contactSupportButton,
                    ...style.contactSupportOrChatButton,
                  }}
                  onClick={() =>
                    (window.location.href = `tel:${supportNumber}`)
                  }
                >
                  <GetImages name="ContactSupportIcon" width="16" height="16" />
                </Grid>
              )}
              {status === IOrderStatus.CANCELLED && (
                <Grid item sx={style.prescriptionButton}>
                  <Button
                    variant={IButtonVariant.WHITE}
                    btnTrigger={bookAgainHandler}
                  >
                    <GetImages
                      name={"MedicineSmallIcon"}
                      width="26"
                      height="20"
                    />
                    <FormattedMessage
                      id="book_again"
                      defaultMessage="Book Again"
                    />
                  </Button>
                </Grid>
              )}
            </Grid>
          </Stack>
        </Grid>
      </Stack>
      <Confirmation
        isOpen={isCancelModalOpen}
        closeHandler={cancelOrderModalHandler}
        title={"cancel_order"}
        alterMessage="cancel_order_alert_message"
        actionHandler={medicineOrderCancelHandler}
      />
      {isPrescriptionModalOpen && (
        <PrescriptionPreview
          isOpen={isPrescriptionModalOpen}
          closeHandler={() => setIsPrescriptionModalOpen(false)}
          medicalRecords={{
            url: s3Url,
          }}
          actionHandler={() => setIsPrescriptionModalOpen(false)}
          btnLabelId="done"
          headerTitleId="prescription"
        />
      )}
    </>
  );
};

export default MedicineCard;
